import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import { StreamWSMessage } from '@rsos/hermes-js/src/typings/common';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_TRANSFER,
  CONVERSATION_TRANSFER_UPDATE,
  CONVERSATION_UPDATE,
  ConversationMessage,
  ConversationStart,
} from '@rsos/hermes-js/src/typings/conversation';
import {
  addConversation,
  setConversationTransfer,
  updateConversation,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { addRCSConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { dismissToastByID } from '@rsos/utils/toastUtils';
import { addLocToQueue } from '../../../actions';
import useHasAccessToAdminLineRequests from '../../../hooks/useHasAccessToAdminLineRequests';
import { convertKeysToCamelCase } from './helpers';

const conversationTransferMessageSchema = z.object({
  type: z.union([
    z.literal(CONVERSATION_TRANSFER),
    z.literal(CONVERSATION_TRANSFER_UPDATE),
  ]),
  body: z
    .object({
      caller_id: z.string(),
      conversation_id: z.string(),
      transfer_status: z.enum(['pending', 'accepted', 'timed_out']),
      initiating_authority_display_name: z.string().optional(),
      initiating_authority_account_id: z.string().optional(),
      receiving_authority_display_name: z.string().optional(),
      receiving_authority_account_id: z.string().optional(),
    })
    .passthrough(),
});

const conversationStatuses = new Set([
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  CONVERSATION_END,
  CONVERSATION_TRANSFER,
  CONVERSATION_TRANSFER_UPDATE,
]);

const normalizeLocation = (body: ConversationMessage['body']) => {
  const baseLocation = {
    caller_id: body.caller_id,
    call_start_time: body.conversation_start_time,
    source: body.source,
  };

  if ('location_data' in body) {
    return {
      ...baseLocation,
      ...body.location_data,
    };
  }

  return baseLocation;
};

const isConversationMessage = (
  message: StreamWSMessage,
): message is ConversationMessage => {
  return conversationStatuses.has(message.type);
};

const useHandleConversationMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const hasAdminLineRequests = useHasAccessToAdminLineRequests();

  const handleConversationMessage = useCallback(
    (message: StreamWSMessage) => {
      if (isConversationMessage(message)) {
        const body = message.body;
        const source = body.source;
        const isUnAuthAdmin =
          source === SOURCE_ADMIN_SMS && !hasAdminLineRequests;

        if (isUnAuthAdmin) {
          return;
        }

        const normalizedLocation = normalizeLocation(body);
        dispatch(addLocToQueue(normalizedLocation));

        let payload = body;
        if (source === SOURCE_TCAPI) {
          // Can remove when tcapi switches over to snake_case
          const snakeCaseExtraInfo = convertKeysToCamelCase(body.extra_info);
          payload = { ...body, ...snakeCaseExtraInfo };

          if (message.type === CONVERSATION_UPDATE) {
            dismissToastByID(`conversations-toast-${body.caller_id}`);
          }
        }

        if (
          [CONVERSATION_TRANSFER, CONVERSATION_TRANSFER_UPDATE].includes(
            message.type,
          )
        ) {
          const parsed = conversationTransferMessageSchema.safeParse(message);
          if (!parsed.success) {
            // eslint-disable-next-line no-console -- logging for sentry
            console.error(
              `Failed to parse ${message.type} message:`,
              parsed.error,
            );
            return;
          }
          dispatch(
            setConversationTransfer({
              callerID: parsed.data.body.caller_id,
              transfer: {
                status: parsed.data.body.transfer_status,
                receivingAuthorityDisplayName:
                  parsed.data.body.receiving_authority_display_name ?? null,
                receivingAuthorityAccountId:
                  parsed.data.body.receiving_authority_account_id ?? null,
                initiatingAuthorityDisplayName:
                  parsed.data.body.initiating_authority_display_name ?? null,
                initiatingAuthorityAccountId:
                  parsed.data.body.initiating_authority_account_id ?? null,
              },
            }),
          );
          return;
        }

        if (source === SOURCE_RCS) {
          dispatch(addRCSConversation(body.session_metadata));
        } else if (message.type === CONVERSATION_START) {
          // Can remove "as" once payload is removed
          dispatch(addConversation(payload as ConversationStart['body']));
        } else if (
          message.type === CONVERSATION_UPDATE ||
          message.type === CONVERSATION_END
        ) {
          dispatch(
            updateConversation({ body: payload, messageType: message.type }),
          );
        }
      }
    },
    [dispatch, hasAdminLineRequests],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleConversationMessage: handleConversationMessage as unknown as (
          message: Record<string, unknown>,
        ) => void,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleConversationMessage');
      };
    }
  }, [StreamWS, handleConversationMessage]);
};

export default useHandleConversationMessage;
